import {useMemo} from "react";
import moment from "moment";
import {
	Checkbox,
	FormControlLabel,
	ToggleButton,
	ToggleButtonGroup,
} from "@mui/material";
import VLFDatePicker from "@/components/Input/DatePicker";
import {
	summarizeTrainings,
	trainingWeeks,
} from "@/utils/playerTrainingStatistics";
import "./index.scss";

const number = (value) =>
	value == null
		? "—"
		: value.toLocaleString("it-IT", {maximumFractionDigits: 1});

export default function PlayerTrainingStatistics({
	records,
	loading,
	filter,
	setFilter,
	date,
	setDate,
	includePreparation,
	setIncludePreparation,
}) {
	const totals = useMemo(() => summarizeTrainings(records), [records]);
	const weeks = useMemo(() => trainingWeeks(records), [records]);
	const sessions = useMemo(
		() =>
			[...records].sort(
				(a, b) => new Date(b.startDate) - new Date(a.startDate),
			),
		[records],
	);
	const tiles = [
		[
			totals.attendances + "/" + totals.total,
			"Presenze",
			totals.unknown + " presenze non registrate",
		],
		[
			number(totals.minutes),
			"Minuti frequentati",
			"Durata su " +
				totals.minutesCount +
				"/" +
				totals.attendances +
				" presenze",
		],
		[
			totals.absences,
			"Assenze",
			totals.injuries + " sedute con stato Infortunato",
		],
		[totals.otherRosters, "Con altre rose", "Sole sedute frequentate"],
		[
			number(totals.rpe),
			"RPE medio",
			"RPE su " + totals.rpeCount + "/" + totals.attendances + " presenze",
		],
		[
			number(totals.load),
			"Carico totale",
			"Carico su " + totals.loadCount + "/" + totals.attendances + " presenze",
		],
	];
	return (
		<section
			className="player-training-statistics"
			aria-label="Dati degli allenamenti"
		>
			<div className="player-training-statistics__toolbar">
				<ToggleButtonGroup
					size="small"
					exclusive
					value={filter}
					onChange={(_, value) => value && setFilter(value)}
					aria-label="Raggruppamento allenamenti"
				>
					<ToggleButton value="weekly">Riepilogo mensile</ToggleButton>
					<ToggleButton value="daily">Sedute della settimana</ToggleButton>
				</ToggleButtonGroup>
				<div className="player-training-statistics__date">
					<VLFDatePicker
						type={filter === "daily" ? "week" : "month"}
						customDateFormat={filter === "daily" ? "w/yyyy" : "MM/yyyy"}
						value={date}
						onChange={setDate}
						size="small"
					/>
				</div>
				<FormControlLabel
					control={
						<Checkbox
							size="small"
							checked={includePreparation}
							onChange={(event) => setIncludePreparation(event.target.checked)}
						/>
					}
					label="Includi preparazione (+30′ per seduta)"
				/>
			</div>
			<p className="player-training-statistics__note">
				Solo sedute trascorse della stagione selezionata. I 30 minuti si
				aggiungono ai presenti per durata e carico, senza modificare gli
				allenamenti salvati.
			</p>
			{loading ? (
				<p role="status">Caricamento statistiche degli allenamenti…</p>
			) : (
				<>
					<div className="player-training-statistics__summary">
						{tiles.map(([value, label, note]) => (
							<div className="player-training-statistics__tile" key={label}>
								<strong>{value}</strong>
								<span>{label}</span>
								<small>{note}</small>
							</div>
						))}
					</div>
					<p className="player-training-statistics__note">
						RPE su scala 1–10; carico = minuti frequentati × RPE. I dati
						mancanti restano «—». «Con altre rose» confronta la rosa della
						seduta con le assegnazioni del giocatore nella stagione.
						{totals.withoutRoster > 0 &&
							` ${totals.withoutRoster} presenze senza rosa indicata escluse da questo conteggio.`}
					</p>
					{!records.length ? (
						<p>Nessuna seduta per il periodo selezionato.</p>
					) : (
						<div className="player-training-statistics__table-wrapper">
							<table>
								<caption className="visually-hidden">
									{filter === "weekly"
										? "Riepilogo settimanale degli allenamenti"
										: "Dettaglio delle sedute"}
								</caption>
								<thead>
									<tr>
										{(filter === "weekly"
											? [
													"Settimana",
													"Presenze",
													"Assenze",
													"Infortunio",
													"Non registrate",
													"Altre rose",
													"Minuti",
													"RPE medio",
													"Carico",
												]
											: ["Data", "Rosa", "Presenza", "Minuti", "RPE", "Carico"]
										).map((label) => (
											<th scope="col" key={label}>
												{label}
											</th>
										))}
									</tr>
								</thead>
								<tbody>
									{filter === "weekly"
										? weeks.map((row) => (
												<tr key={row.date}>
													<td>
														{moment(row.date).format("DD/MM")} –{" "}
														{moment(row.date)
															.endOf("isoWeek")
															.format("DD/MM/YYYY")}
													</td>
													<td>
														{row.attendances}/{row.total}
													</td>
													<td>{row.absences}</td>
													<td>{row.injuries}</td>
													<td>{row.unknown}</td>
													<td>{row.otherRosters}</td>
													<td>
														{number(row.minutes)}
														<small>
															{row.minutesCount}/{row.attendances} presenze
														</small>
													</td>
													<td>
														{number(row.rpe)}
														<small>
															{row.rpeCount}/{row.attendances} presenze
														</small>
													</td>
													<td>
														{number(row.load)}
														<small>
															{row.loadCount}/{row.attendances} presenze
														</small>
													</td>
												</tr>
											))
										: sessions.map((row) => (
												<tr key={row.trainingId}>
													<td>
														{moment(row.startDate).format("DD/MM/YYYY")}
														<small>
															{moment(row.startDate).format("HH:mm")}
														</small>
													</td>
													<td>
														{row.rosterName || "Rosa non indicata"}
														{row.rosterId && row.otherConvocations && (
															<small>Altra rosa</small>
														)}
													</td>
													<td>
														<span
															className={
																"player-training-statistics__status " +
																(row.presenceStatus === "Presente"
																	? "present"
																	: "")
															}
														>
															{row.presenceStatus === "Non Indicato" ||
															!row.presenceStatus
																? "Non registrata"
																: row.presenceStatus}
														</span>
													</td>
													<td>
														{number(row.presenceDuration)}
														{row.preparationMinutes > 0 && (
															<small>
																Inclusi {row.preparationMinutes}′ di
																preparazione
															</small>
														)}
													</td>
													<td>{number(row.playerRpe)}</td>
													<td>{number(row.trainingLoad)}</td>
												</tr>
											))}
								</tbody>
							</table>
						</div>
					)}
				</>
			)}
		</section>
	);
}
